import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class DrAuthService {
  private refreshTimeout: any;
  private url = environment.graphqlUrl;
  private baseKey = `CognitoIdentityServiceProvider.${environment.clientId}`;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private tokenService: TokenService
  ) {}

  private getEmail(): string | null {
    return localStorage.getItem(`${this.baseKey}.LastAuthUser`);
  }

  private getPrimaryKey(email: string): string {
    return `${this.baseKey}.${email}`;
  }

  private getSecondaryKey(email: string): string {
    return `CognitoIdentityServiceProvider.${environment.clientIdSecondary}.${email}.secondary`;
  }

  private getToken(key: string, suffix: string): string | null {
    return localStorage.getItem(`${key}.${suffix}`);
  }

  get expireAt(): number {
    const email = this.getEmail();
    if (!email) return 0;

    const key = this.getPrimaryKey(email);
    const expiresAt = this.getToken(key, 'expiresat');
    return expiresAt ? +expiresAt - 60 : 0;
  }

  shouldRefresh(): boolean {
    const exp = this.expireAt;
    return exp !== 0 && exp < Date.now() / 1000;
  }

  refreshToken(): void {
    const email = this.getEmail();
    if (!email) return;

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'refreshToken');
    const secondaryToken = this.getToken(secondaryKey, 'refreshToken');

    if (!primaryToken && !secondaryToken) return;

    const { drRegion, drRegionSecondary } = environment;

    const headers = {
      Authorization: 'NO_AUTHENTICATION',
      AuthorizationSecondary: 'NO_AUTHENTICATION',
    };

    this.http
      .post<any>(
        `${this.url}`,
        {
          query: `
      mutation ($refreshtoken: String!, $refreshtokensecondary: String!) {
        refreshToken(refreshtoken: $refreshtoken, refreshtokensecondary: $refreshtokensecondary) {
          ${drRegion} {
            success
            idtoken
            accesstoken
            expiresat
            error {
              code
              message
            }
          }
          ${drRegionSecondary} {
            success
            idtoken
            accesstoken
            expiresat
            error {
              code
              message
            }
          }
        }
      }
    `,
          variables: {
            refreshtoken: primaryToken || "",
            refreshtokensecondary: secondaryToken || "",
          },
        },
        { headers }
      )
      .pipe(
        map((res) => {
          if (res?.data && res?.data?.refreshToken) {
            const tokens = res.data.refreshToken;
            // Use primary token if available, otherwise fallback to secondary
            let primaryTokenData = tokens[drRegion];
            let secondaryTokenData = tokens[drRegionSecondary];

            if (!primaryTokenData && secondaryTokenData) {
              console.warn(
                `Primary region (${drRegion}) token not available during refresh, using secondary`
              );
              primaryTokenData = secondaryTokenData;
              secondaryTokenData = null;
            }

            this.tokenService.setTokens(
              primaryTokenData,
              secondaryTokenData,
              email
            );
            this.init();
          } else if (res?.errors?.length > 0) {
            throw new Error(res.errors.map((e: any) => e.message).join(', '));
          }
        })
      )
      .subscribe({
        error: console.error,
      });
  }

  scheduleRefresh(): void {
    const delay = (this.expireAt - Date.now() / 1000) * 1000;
    if (delay <= 0) return;

    this.clearTimer();

    console.log(`Scheduling token refresh in ${delay}ms`);

    this.ngZone.runOutsideAngular(() => {
      this.refreshTimeout = setTimeout(() => {
        this.ngZone.run(() => this.refreshToken());
      }, delay);
    });
  }

  clearTimer(): void {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  async init(): Promise<void> {
    console.log('DrAuthService initialized');
    if (this.shouldRefresh()) {
      await this.refreshToken();
    }
    this.scheduleRefresh();
    this.handshake();
  }

  handshake(): void {
    const email = this.getEmail();
    if (!email) return;

    const primaryKey = this.getPrimaryKey(email);
    const secondaryKey = this.getSecondaryKey(email);

    const primaryToken = this.getToken(primaryKey, 'idToken');
    const secondaryToken = this.getToken(secondaryKey, 'idToken');

    if (!!primaryToken !== !!secondaryToken) {
      const { drRegion, drRegionSecondary } = environment;

      this.http
        .post<any>(`${this.url}`, {
          query: `
                query handshake {
                        handshake {
                            error {
                                code
                                message
                            }
                            ${drRegion} {
                                success
                                idtoken
                                accesstoken
                                refreshtoken
                                expiresat
                            }
                            ${drRegionSecondary} {
                                success
                                idtoken
                                accesstoken
                                refreshtoken
                                expiresat
                            }
                        }
                    }`,
        })
        .pipe(
          map((res) => {
            if (res?.data && res?.data?.handshake) {
              const tokens = res.data.handshake;
              // Use primary token if available, otherwise fallback to secondary
              let primaryTokenData = tokens[drRegion];
              let secondaryTokenData = tokens[drRegionSecondary];

              if (
                (!primaryTokenData || !primaryTokenData.success) &&
                secondaryTokenData &&
                secondaryTokenData.success
              ) {
                console.warn(
                  `Primary region (${drRegion}) token not available during handshake, using secondary`
                );
                primaryTokenData = secondaryTokenData;
                secondaryTokenData = null;
              }

              this.tokenService.setTokens(
                primaryTokenData,
                secondaryTokenData,
                email
              );
            } else if (res?.errors?.length > 0) {
              throw new Error(res.errors.map((e: any) => e.message).join(', '));
            }
          })
        )
        .subscribe({
          error: console.error,
        });
    }
  }
}
